import {
  enqueueCartMutation,
  requestCartJson,
  collectCartSectionIds,
  applyCartSectionsResponse,
  extractCartItemCountFromSections,
  setCartItemsLocked,
} from '@theme/cart-section-updater';
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Gift with purchase.
 *
 * Keeps exactly one gift line in the cart while the qualifying product is in it, unless the shopper
 * declined the gift (cart attribute `_gwp_declined`, so the choice survives reloads and is shared by the
 * drawer, the cart page and the product page toggle). The gift is added as a one-time line (no selling
 * plan) with the `_gwp` property. Settings come from `#gwp-config`, rendered in `snippets/scripts.liquid`.
 *
 * Cart sections are morphed without dispatching `cart:update`, because that event auto-opens the drawer.
 */

/**
 * @typedef {object} CartItem
 * @property {string} key
 * @property {number} product_id
 * @property {number} variant_id
 * @property {number} quantity
 * @property {Record<string, string>} [properties]
 *
 * @typedef {object} Cart
 * @property {CartItem[]} items
 * @property {Record<string, string>} [attributes]
 */

const configElement = document.getElementById('gwp-config');
const config = configElement ? JSON.parse(configElement.textContent || '{}') : null;

const DECLINED_ATTRIBUTE = '_gwp_declined';

let syncQueued = false;

if (config) {
  document.addEventListener(ThemeEvents.cartUpdate, queueSync);
  document.addEventListener('click', handleClick);
  document.addEventListener('change', handleToggle);
  queueSync();
}

/**
 * Runs one sync after the cart mutations already queued. Repeated calls before it starts collapse into one.
 */
function queueSync() {
  if (syncQueued) return;
  syncQueued = true;

  enqueueCartMutation(async () => {
    syncQueued = false;
    try {
      await sync();
    } catch (error) {
      console.error(error);
    }
  });
}

/**
 * Brings the gift line in line with the cart. Must run inside the cart mutation queue.
 * @param {Cart} [cart] - A fresh cart, when the caller already has one.
 */
async function sync(cart) {
  cart ??= /** @type {Cart} */ (await requestCart());
  syncModules(isDeclined(cart));

  const giftLines = cart.items.filter((item) => item.variant_id === config.giftVariantId);
  const giftQuantity = giftLines.reduce((total, item) => total + item.quantity, 0);
  const hasQualifyingProduct = cart.items.some((item) => item.product_id === config.qualifyingProductId);
  const wantsGift = hasQualifyingProduct && !isDeclined(cart) && config.giftAvailable;

  if (!wantsGift) {
    if (giftLines.length > 0) await updateCart({ updates: zeroQuantities(giftLines) });
    return;
  }

  if (giftQuantity === 0) {
    await addGift();
    return;
  }

  if (giftQuantity > 1) {
    const keep = giftLines.find((item) => item.properties?._gwp) ?? giftLines[0];
    const updates = zeroQuantities(giftLines);
    updates[keep.key] = 1;

    await updateCart({ updates });
    showLimitToast();
  }
}

async function addGift() {
  const sections = collectCartSectionIds();
  const response = await requestCartJson(Theme.routes.cart_add_url, {
    items: [{ id: config.giftVariantId, quantity: 1, properties: { _gwp: 'true' } }],
    ...sectionsParams(sections),
  });

  if (response.status) {
    // Out of stock (or otherwise unavailable): show the sold-out state instead of an error.
    config.giftAvailable = false;
    document.querySelectorAll('[data-gwp-module]').forEach((module) => module.classList.add('gwp-module--sold-out'));
    for (const sectionId of sections.split(',').filter(Boolean)) {
      sectionRenderer.renderSection(sectionId, { cache: false });
    }
    return;
  }

  renderCartSections(response);
}

/**
 * @param {object} body - `/cart/update.js` body.
 * @returns {Promise<Cart>}
 */
async function updateCart(body) {
  const response = await requestCartJson(Theme.routes.cart_update_url, {
    ...body,
    ...sectionsParams(collectCartSectionIds()),
  });

  if (response.status) throw new Error(response.description || 'Cart update failed');

  renderCartSections(response);
  syncModules(isDeclined(response));
  return response;
}

/**
 * @param {{ sections?: Record<string, string> }} response
 */
function renderCartSections(response) {
  if (!response.sections) return;

  const sourceSectionId = Object.keys(response.sections)[0];
  applyCartSectionsResponse(response, { sourceSectionId, source: 'gwp', dispatchEvent: false });

  const itemCount = extractCartItemCountFromSections(response.sections, sourceSectionId);
  document.querySelectorAll('cart-icon').forEach((icon) => {
    /** @type {any} */ (icon).renderCartBubble?.(itemCount, false, false);
  });
}

/**
 * Remove / add back buttons on the cart gift line and the cart notice.
 * @param {MouseEvent} event
 */
function handleClick(event) {
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest('[data-gwp-remove], [data-gwp-add]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  event.preventDefault();
  const declining = button.hasAttribute('data-gwp-remove');

  setBusy(button, true);
  setCartItemsLocked(true);

  enqueueCartMutation(async () => {
    try {
      if (declining) {
        const cart = /** @type {Cart} */ (await requestCart());
        const giftLines = cart.items.filter((item) => item.variant_id === config.giftVariantId);
        await updateCart({ updates: zeroQuantities(giftLines), attributes: { [DECLINED_ATTRIBUTE]: 'true' } });
      } else {
        await sync(await setDeclined(false));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setBusy(button, false);
      setCartItemsLocked(false);
    }
  });
}

/**
 * Product page toggle: on = include the gift, off = decline it.
 * @param {Event} event
 */
function handleToggle(event) {
  const toggle = event.target;
  if (!(toggle instanceof HTMLInputElement) || !toggle.hasAttribute('data-gwp-toggle')) return;

  const declined = !toggle.checked;
  syncModules(declined);
  setBusy(toggle, true);

  enqueueCartMutation(async () => {
    try {
      await sync(await setDeclined(declined));
    } catch (error) {
      console.error(error);
    } finally {
      setBusy(toggle, false);
    }
  });
}

/**
 * @param {boolean} declined
 * @returns {Promise<Cart>}
 */
async function setDeclined(declined) {
  const cart = await requestCartJson(Theme.routes.cart_update_url, {
    attributes: { [DECLINED_ATTRIBUTE]: declined ? 'true' : '' },
  });

  if (cart.status) throw new Error(cart.description || 'Cart update failed');
  return /** @type {Cart} */ (cart);
}

/**
 * Mirrors the declined state onto every product page gift module.
 * @param {boolean} declined
 */
function syncModules(declined) {
  document.querySelectorAll('[data-gwp-module]').forEach((module) => {
    module.classList.toggle('gwp-module--declined', declined);

    const toggle = module.querySelector('[data-gwp-toggle]');
    if (toggle instanceof HTMLInputElement) toggle.checked = !declined;
  });
}

function showLimitToast() {
  const toast = document.createElement('div');
  toast.className = 'gwp-toast';
  toast.setAttribute('role', 'status');
  // A popover renders in the top layer, so the toast stays visible above the open cart drawer dialog.
  toast.setAttribute('popover', 'manual');
  toast.innerHTML = `
    <svg class="gwp-toast__icon" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r="10" fill="currentColor"/>
      <path d="M10 9v4.5M10 6.5v.01" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/>
    </svg>
    <div>
      <p class="gwp-toast__title"></p>
      <p class="gwp-toast__text"></p>
    </div>
  `;
  /** @type {HTMLElement} */ (toast.querySelector('.gwp-toast__title')).textContent = config.limitTitle;
  /** @type {HTMLElement} */ (toast.querySelector('.gwp-toast__text')).textContent = config.limitText;

  document.body.append(toast);
  toast.showPopover?.();
  setTimeout(() => toast.remove(), 5000);
}

/**
 * @param {HTMLButtonElement | HTMLInputElement} element
 * @param {boolean} busy
 */
function setBusy(element, busy) {
  element.disabled = busy;
  element.toggleAttribute('aria-busy', busy);
}

/**
 * Section Rendering params for the cart sections on the page (none when the page has no cart).
 * @param {string} sections - Comma separated section ids.
 */
function sectionsParams(sections) {
  return sections ? { sections, sections_url: window.location.pathname } : {};
}

/**
 * @param {CartItem[]} items
 * @returns {Record<string, number>}
 */
function zeroQuantities(items) {
  return Object.fromEntries(items.map((item) => [item.key, 0]));
}

/**
 * @param {Cart} cart
 */
function isDeclined(cart) {
  return cart.attributes?.[DECLINED_ATTRIBUTE] === 'true';
}

async function requestCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
  return response.json();
}
